import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const IMG = 'https://imagedelivery.net/xaKlCos5cTg_1RWzIu_h-A';

const IMAGES = {
  comedorCuadrado: `${IMG}/c2b3bf20-961f-48d8-e2e4-dd1dd2d0ec00/public`,
  mesaRedonda:     `${IMG}/57a3d564-5eae-4d46-bbdf-ac701763f900/public`,
  sillas:          `${IMG}/08bbce40-0101-4a0b-567b-39a2073b8b00/public`,
  liveEdge:        `${IMG}/31da9c3d-34b7-4ec6-d370-71125f17e300/publicContain`,
  terraza:         `${IMG}/f5741ec8-762a-4cb7-7939-61490a958300/public`,
  bancoBarra:      `${IMG}/28d71341-3be2-4184-fcc2-e090cf76ef00/public`,
  contemporaneo:   `${IMG}/0800d90b-bc45-42a6-43c1-e7f886ba7100/public`,
};

const EASE = 'cubic-bezier(0.23,1,0.32,1)';

function useReveal(ref, options = {}) {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const el = ref.current;
    if (!el) return;
    const targets = el.querySelectorAll('[data-reveal]');
    if (!targets.length) return;
    targets.forEach((t, i) => {
      gsap.fromTo(
        t,
        { opacity: 0, y: options.y ?? 24 },
        {
          opacity: 1,
          y: 0,
          duration: options.duration ?? 0.65,
          delay: i * (options.stagger ?? 0.1),
          ease: EASE,
          scrollTrigger: {
            trigger: t,
            start: 'top 88%',
            toggleActions: 'play none none none',
          },
        }
      );
    });
    return () => ScrollTrigger.getAll().forEach((st) => st.kill());
  }, [ref, options]);
}

/* ─── Hero ─── */
function Hero() {
  const ref = useRef(null);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const el = ref.current;
    if (!el) return;
    const tl = gsap.timeline();
    tl.fromTo(el.querySelector('h1'), { opacity: 0, y: 32 }, { opacity: 1, y: 0, duration: 0.8, ease: EASE })
      .fromTo(el.querySelector('p'),  { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.65, ease: EASE }, '-=0.45')
      .fromTo(el.querySelector('a'),  { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 0.55, ease: EASE }, '-=0.35');
    return () => tl.kill();
  }, []);

  return (
    <section
      ref={ref}
      className="relative flex items-center overflow-hidden"
      style={{ minHeight: '60vh' }}
      aria-label="Mobiliario comercial para restaurantes"
    >
      <div className="absolute inset-0">
        <img
          src={IMAGES.comedorCuadrado}
          alt="Comedor cuadrado para 8 personas — Del Sol Muebles"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-wood/85 via-wood/60 to-wood/30" />
      </div>

      <div className="relative z-10 container-custom py-24">
        <div className="max-w-2xl">
          <span className="font-sans text-xs tracking-[0.3em] uppercase text-gold mb-5 block">
            Proyectos Comerciales · Tonalá, Jalisco
          </span>
          <h1 className="font-serif text-display-xs md:text-display-sm text-white mb-6 leading-tight">
            Mobiliario para Restaurantes, Bares y Hoteles
          </h1>
          <div className="w-14 h-px bg-gold mb-6" />
          <p className="font-sans text-base text-cream/80 leading-relaxed mb-10 max-w-lg">
            Fabricación en volumen · Diseño personalizado · Entrega en 2 semanas
          </p>
          <a
            href="https://wa.me/523338283729?text=Hola%20Del%20Sol%20Muebles%2C%20quiero%20cotizar%20mobiliario%20comercial"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            data-magnetic
          >
            Solicitar Cotización Comercial
          </a>
        </div>
      </div>
    </section>
  );
}

/* ─── Intro / Why Us ─── */
function IntroSection() {
  const ref = useRef(null);
  useReveal(ref);
  const pillars = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
        </svg>
      ),
      title: 'Fabricación en Volumen',
      body: 'Desde 10 hasta 500+ piezas sin perder calidad artesanal. Capacidad de producción propia en Tonalá.',
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />
        </svg>
      ),
      title: 'Diseño Personalizado',
      body: 'Adaptamos medidas, madera, acabado y tapizado a tu concepto gastronómico. Sin moldes fijos.',
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
        </svg>
      ),
      title: 'Entrega a Todo México',
      body: 'Flete coordinado con embalaje especializado para negocios. Llegamos a cualquier ciudad de la república.',
    },
  ];

  return (
    <section ref={ref} className="section-padding bg-cream-light">
      <div className="container-custom">
        <div className="text-center mb-16" data-reveal>
          <span className="font-sans text-xs tracking-[0.25em] uppercase text-terracotta/80 mb-4 block">
            Tu Proveedor en Tonalá
          </span>
          <h2 className="font-serif text-display-xs md:text-display-sm text-wood">
            Somos tu Proveedor de Mobiliario Comercial en Tonalá
          </h2>
          <div className="w-12 h-px bg-gold mx-auto mt-6" />
          <p className="font-sans text-base text-taupe mt-6 max-w-2xl mx-auto leading-relaxed">
            Más de 20 años fabricando muebles de madera maciza en el corazón artesanal de México.
            Trabajamos con restaurantes, hoteles, bares y cadenas gastronómicas en todo el país.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {pillars.map((p, i) => (
            <div
              key={i}
              data-reveal
              className="bg-cream p-8 rounded-sm border border-gold/10 hover:border-gold/30 transition-colors duration-300 text-center"
            >
              <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-terracotta/10 text-terracotta mb-5">
                {p.icon}
              </span>
              <h3 className="font-serif text-xl text-wood mb-3">{p.title}</h3>
              <p className="font-sans text-sm text-taupe leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Products for Restaurants (AEO critical) ─── */
function ProductsSection() {
  const ref = useRef(null);
  useReveal(ref);

  const products = [
    {
      question: 'Mesas para restaurante',
      answer: 'Fabricamos mesas cuadradas, redondas y rectangulares en madera maciza (parota, mezquite, encino). Disponibles en tamaños para 2, 4, 6 y 8 comensales. Acabados: natural, barniz mate, barniz brillante o pintado.',
      images: [IMAGES.comedorCuadrado, IMAGES.mesaRedonda],
      alts: ['Mesa cuadrada para restaurante 8 personas', 'Mesa redonda para restaurante'],
    },
    {
      question: 'Sillas de comedor comerciales',
      answer: 'Sillas tapizadas en piel o tela, sillas tejidas en ratán, sillas de madera maciza y sillas con respaldo decorativo. Resistentes al uso intensivo diario con estructura reforzada.',
      images: [IMAGES.sillas, IMAGES.contemporaneo],
      alts: ['Sillas tejidas para restaurante', 'Silla contemporánea para comedor comercial'],
    },
    {
      question: 'Bancos y bancas para barra',
      answer: 'Taburetes altos y bancos de barra en madera maciza con asiento tapizado o de madera. Alturas de 65 cm y 75 cm. Ideales para barras de bar, cafeterías y áreas lounge.',
      images: [IMAGES.bancoBarra],
      alts: ['Banco de barra para restaurante en madera'],
    },
    {
      question: 'Mesas altas y taburetes para bar',
      answer: 'Conjuntos de mesa alta (poseur tables) y taburetes para áreas de bar, lounges y zonas de pie. Madera + estructura de hierro forjado disponible.',
      images: [IMAGES.liveEdge],
      alts: ['Mesa tipo live edge para bar'],
    },
    {
      question: 'Equipales (tradición mexicana)',
      answer: 'Fabricamos equipales de cuero genuino en estilos tradicional y contemporáneo. Butacas, taburetes y sillones. El mueble más icónico de la cocina mexicana.',
      images: [],
      alts: [],
    },
    {
      question: 'Muebles para terraza de restaurante',
      answer: 'Mesas y sillas tratadas para exterior con maderas resistentes como teca certificada o pino tratado. Acabados UV y resistencia a la intemperie.',
      images: [IMAGES.terraza],
      alts: ['Muebles para terraza de restaurante'],
    },
    {
      question: 'Estaciones de trabajo y barras personalizadas',
      answer: 'Barras de servicio, estaciones de hostess, aparadores y buffets a medida. Diseño según plano de tu espacio.',
      images: [],
      alts: [],
    },
  ];

  return (
    <section ref={ref} className="section-padding bg-cream" itemScope itemType="https://schema.org/ItemList">
      <div className="container-custom">
        <div className="text-center mb-16" data-reveal>
          <span className="font-sans text-xs tracking-[0.25em] uppercase text-terracotta/80 mb-4 block">
            Catálogo Comercial
          </span>
          <h2 className="font-serif text-display-xs md:text-display-sm text-wood" itemProp="name">
            ¿Qué muebles fabricamos para restaurantes?
          </h2>
          <div className="w-12 h-px bg-gold mx-auto mt-6" />
        </div>

        <div className="space-y-16">
          {products.map((prod, i) => (
            <article
              key={i}
              data-reveal
              className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start"
              itemScope
              itemType="https://schema.org/Product"
            >
              <div className={i % 2 === 1 ? 'order-1 lg:order-2' : ''}>
                <h3 className="font-serif text-2xl text-wood mb-3" itemProp="name">{prod.question}</h3>
                <div className="w-8 h-px bg-gold mb-4" />
                <p className="font-sans text-sm text-taupe leading-relaxed" itemProp="description">{prod.answer}</p>
                <a
                  href={`https://wa.me/523338283729?text=Hola%2C%20me%20interesan%20${encodeURIComponent(prod.question)}%20para%20mi%20negocio`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-5 font-sans text-xs tracking-widest uppercase text-terracotta hover:text-terracotta-dark transition-colors duration-200"
                >
                  <span>Cotizar este producto</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </a>
              </div>

              {prod.images.length > 0 && (
                <div className={`grid gap-3 ${prod.images.length > 1 ? 'grid-cols-2' : 'grid-cols-1'} ${i % 2 === 1 ? 'order-2 lg:order-1' : ''}`}>
                  {prod.images.map((src, j) => (
                    <div key={j} className="overflow-hidden rounded-sm aspect-[4/3] bg-cream-light">
                      <img
                        src={src}
                        alt={prod.alts[j]}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                        loading="lazy"
                        itemProp="image"
                      />
                    </div>
                  ))}
                </div>
              )}
              {prod.images.length === 0 && (
                <div className={`flex items-center justify-center rounded-sm bg-cream-light border border-gold/10 aspect-[4/3] ${i % 2 === 1 ? 'order-2 lg:order-1' : ''}`}>
                  <span className="font-sans text-xs text-taupe tracking-widest uppercase">Imagen disponible al cotizar</span>
                </div>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Featured Projects ─── */
function ProjectsSection() {
  const ref = useRef(null);
  useReveal(ref);

  const featured = [
    {
      name: 'Restaurante El Jardín, Guadalajara',
      detail: '20 mesas cuadradas + 80 sillas tapizadas en parota natural',
      img: IMAGES.comedorCuadrado,
      tag: 'Restaurante Fine Dining',
    },
    {
      name: 'Bar The Wood, CDMX',
      detail: '15 mesas altas + 45 taburetes de barra en mezquite con base de hierro',
      img: IMAGES.bancoBarra,
      tag: 'Bar & Lounge',
    },
    {
      name: 'Taquería La Fondita — Cadena',
      detail: '30 sucursales equipadas · 900 piezas · entrega en fases',
      img: IMAGES.sillas,
      tag: 'Cadena de Restaurantes',
    },
  ];

  return (
    <section ref={ref} className="section-padding bg-wood">
      <div className="container-custom">
        <div className="text-center mb-16" data-reveal>
          <span className="font-sans text-xs tracking-[0.25em] uppercase text-gold mb-4 block">
            Clientes Satisfechos
          </span>
          <h2 className="font-serif text-display-xs md:text-display-sm text-cream">
            Proyectos Comerciales Realizados
          </h2>
          <div className="w-12 h-px bg-gold mx-auto mt-6" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {featured.map((p, i) => (
            <article
              key={i}
              data-reveal
              className="group relative overflow-hidden rounded-sm"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={p.img}
                  alt={p.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-wood via-wood/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <span className="inline-block font-sans text-[10px] tracking-[0.2em] uppercase text-gold mb-2 bg-gold/10 px-2 py-1 rounded-sm">
                  {p.tag}
                </span>
                <h3 className="font-serif text-lg text-white mb-1">{p.name}</h3>
                <p className="font-sans text-xs text-cream/70 leading-relaxed">{p.detail}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── FAQ (AEO) ─── */
function FAQ() {
  const ref = useRef(null);
  useReveal(ref);
  const [open, setOpen] = React.useState(null);

  const faqs = [
    {
      q: '¿Cuánto tiempo tarda fabricar muebles para un restaurante de 20 mesas?',
      a: 'Un pedido de 20 mesas con 80 sillas tarda entre 2 y 3 semanas en producción. Pedidos más grandes (50+ mesas) pueden requerir 4-6 semanas. Damos una fecha exacta de entrega al momento de confirmar el pedido.',
    },
    {
      q: '¿Pueden igualar el diseño de muebles que ya tenemos?',
      a: 'Sí. Si nos envías fotografías o una muestra física del mueble, podemos replicar el diseño en las mismas maderas y acabados, o adaptarlo con los materiales que prefieras. El 70% de nuestros clientes comerciales trabajan con diseño igualado.',
    },
    {
      q: '¿Qué tipo de madera recomiendan para uso comercial intensivo?',
      a: 'Para uso comercial intensivo recomendamos parota (hardwood mexicano de alta densidad), mezquite (extremadamente duro y duradero) o encino. Estas maderas resisten el uso diario, la humedad de cocina y limpieza frecuente. Evitamos pino y madera blanda en proyectos de restaurantes.',
    },
    {
      q: '¿Hacen envío a restaurantes fuera de Jalisco?',
      a: 'Sí, entregamos a toda la república mexicana. Coordinamos el flete con embalaje especializado (espuma de poliuretano + caja de madera para piezas delicadas). El costo de flete se cotiza aparte según destino y volumen.',
    },
    {
      q: '¿Cuál es el pedido mínimo para proyectos comerciales?',
      a: 'El pedido mínimo para precio comercial es de 10 piezas. Para pedidos de 1-9 piezas aplicamos precio de catálogo. A partir de 50 piezas ofrecemos precio mayorista y condiciones de pago a 30 días.',
    },
    {
      q: '¿Ofrecen garantía en muebles para restaurantes?',
      a: 'Sí. Garantía de 1 año contra defectos de fabricación en estructura y acabados. Para madera maciza garantizamos la integridad estructural por tiempo indefinido bajo uso normal.',
    },
  ];

  return (
    <section
      ref={ref}
      className="section-padding bg-cream-light"
      itemScope
      itemType="https://schema.org/FAQPage"
    >
      <div className="container-custom max-w-3xl mx-auto">
        <div className="text-center mb-16" data-reveal>
          <span className="font-sans text-xs tracking-[0.25em] uppercase text-terracotta/80 mb-4 block">
            Respuestas Directas
          </span>
          <h2 className="font-serif text-display-xs md:text-display-sm text-wood">
            Preguntas Frecuentes de Clientes Comerciales
          </h2>
          <div className="w-12 h-px bg-gold mx-auto mt-6" />
        </div>

        <div className="space-y-3" data-reveal>
          {faqs.map((faq, i) => (
            <div
              key={i}
              className="bg-cream border border-gold/10 rounded-sm overflow-hidden"
              itemScope
              itemType="https://schema.org/Question"
            >
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-cream-light transition-colors duration-200"
                aria-expanded={open === i}
              >
                <span className="font-serif text-lg text-wood pr-4" itemProp="name">{faq.q}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`w-5 h-5 flex-shrink-0 text-terracotta transition-transform duration-300 ${open === i ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {open === i && (
                <div
                  className="px-6 pb-6 font-sans text-sm text-taupe leading-relaxed"
                  itemScope
                  itemType="https://schema.org/Answer"
                  itemProp="acceptedAnswer"
                >
                  <span itemProp="text">{faq.a}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Final CTA ─── */
function FinalCTA() {
  const ref = useRef(null);
  useReveal(ref);
  return (
    <section ref={ref} className="py-20 bg-terracotta">
      <div className="container-custom text-center max-w-2xl mx-auto" data-reveal>
        <h2 className="font-serif text-display-xs md:text-display-sm text-white mb-4">
          Cotiza tu Proyecto Comercial
        </h2>
        <p className="font-sans text-base text-white/80 leading-relaxed mb-10">
          Sin compromiso · Respuesta en menos de 24 horas · Asesoría de diseño incluida
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="https://wa.me/523338283729?text=Hola%20Del%20Sol%20Muebles%2C%20quiero%20cotizar%20mobiliario%20para%20mi%20restaurante"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-3 px-8 py-3 bg-white text-terracotta font-sans text-sm font-medium tracking-widest uppercase rounded-sm hover:bg-cream transition-colors duration-300"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
              <path d="M11.999 2C6.477 2 2 6.477 2 12c0 1.89.526 3.658 1.438 5.167L2 22l4.934-1.424A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 11.999 2zm.001 18a7.959 7.959 0 01-4.306-1.263l-.308-.183-3.178.917.898-3.09-.202-.318A7.959 7.959 0 014 12c0-4.418 3.582-8 8-8s8 3.582 8 8-3.582 8-8 8z"/>
            </svg>
            WhatsApp
          </a>
          <a
            href="tel:+523338283729"
            className="inline-flex items-center justify-center gap-3 px-8 py-3 border-2 border-white text-white font-sans text-sm font-medium tracking-widest uppercase rounded-sm hover:bg-white hover:text-terracotta transition-colors duration-300"
          >
            +52 33 3828 3729
          </a>
        </div>
      </div>
    </section>
  );
}

/* ─── Page ─── */
export default function Restaurantes() {
  return (
    <main>
      <Hero />
      <IntroSection />
      <ProductsSection />
      <ProjectsSection />
      <FAQ />
      <FinalCTA />
    </main>
  );
}
